import { Grid, makeStyles, IconButton, Tooltip, Typography, CircularProgress } from '@material-ui/core/';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropzoneArea } from 'material-ui-dropzone';
import { DeleteIcon } from './Icons';
import FileServiceAPI from '../../API/Services/FileServiceAPI';
import { FormContextValues } from 'react-hook-form';
import { convertBytesToMbsOrKbs, blobToFile, getValidator } from '../../Constants/constants';
import CropImageDialog from './CropImageDialog/CropImageDialog';

export interface IDimensions {
	width: number;
	height: number;
}

export interface IModalFileUpload {
	setPicture: (file: File | null) => any;
	methods: FormContextValues<Record<string, any>>;
	isAdditional?: boolean;
	title: string;
	imageDimensions: IDimensions;
	isReadOnly: boolean;
}

const useStyles = makeStyles(() => ({
	root: {
		minHeight: '178px',
		marginTop: '7px',
	},
	picturePreviewContainer: {
		display: 'flex',
		boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.25)',
		marginRight: '10px',
		height: '214px',
		padding: '10px',
		justifyContent: 'center',
		alignItems: 'center',
	},
	pictureThumbnail: {
		minWidth: '269px',
		display: 'flex',
		justifyContent: 'center',
	},
	pictureName: {
		display: 'inline-grid',
		wordBreak: 'break-all',
		fontSize: '20px',
		justifyContent: 'center',
		alignContent: 'center',
		marginRight: '20px',
		width: '190px',
	},
	title: {
		fontWeight: 'bold',
		fontSize: '18px',
	},
	noDrop: {
		cursor: 'no-drop',
		minHeight: '178px',
		marginTop: '7px',
	},
}));

export default function ModalFileUpload(props: IModalFileUpload) {
	const { t } = useTranslation();
	const { setPicture, isAdditional, title, imageDimensions, isReadOnly } = props;
	const [deletePicture, setDeletePicture] = useState(false);
	const [File, setFile] = useState<File | null>();
	const { getValues, register, setValue, reset, unregister, triggerValidation } = props.methods;
	const classes = useStyles(props);

	const [isCropping, setIsCropping] = useState(false);

	const values = getValues();
	const { imageId, id, statsImageId } = values;

	const [imageStr, setImageStr] = useState<any | null>();

	const formValidators = {
		image: getValidator(t, null, null, isAdditional ? false : true, 1),
	};

	const fetchAndSetFile = async (id: number) => {
		const fileResponse = await FileServiceAPI.GetFileUrl(id);
		setImageStr(fileResponse.data.image);
	};

	useEffect(() => {
		async function x() {
			if (imageId && id && !isAdditional) {
				setValue('image', 'true');
				fetchAndSetFile(imageId);
			}
			if (statsImageId && id && isAdditional) {
				fetchAndSetFile(statsImageId);
			}
		}
		x();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [imageId, statsImageId]);

	return (
		<>
			<input name='imageId' ref={register()} hidden={true} />
			{isAdditional && <input name='statsImageId' ref={register()} hidden={true} />}
			<input name={`delete${isAdditional && 'Stats'}File`} ref={register()} hidden={true} />
			{!isAdditional && <input name='image' ref={register(formValidators.image)} hidden={true} />}

			<Grid item xs={6} style={{ paddingRight: '36px' }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '300px', height: '50px' }}>
					<Typography className={classes.title}>{title}</Typography>
					{((isAdditional ? statsImageId : imageId) || File) && !deletePicture && !isCropping && (
						<Grid>
							<Tooltip title={t('table.body.deleteTooltip') || ''}>
								<IconButton
									onClick={() => {
										if (!isReadOnly) {
											setDeletePicture(true);
											setValue(`delete${isAdditional && 'Stats'}File`, true);
											setFile(null);
											setPicture(null);
											if (!isAdditional) {
												reset({ ...getValues(), image: '' });
												unregister(['image']);
											}
										}
									}}
								>
									{!isReadOnly && <DeleteIcon isSelected={true} />}
								</IconButton>
							</Tooltip>
						</Grid>
					)}
				</div>
				{((isAdditional ? statsImageId : imageId) || File) && !deletePicture && !isCropping && (
					<Grid container>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<Grid className={classes.picturePreviewContainer}>
								<Grid className={classes.pictureThumbnail}>
									{imageStr ? (
										<img
											src={File ? URL.createObjectURL(File) : imageStr}
											style={{ maxHeight: '214px', width: 'auto', maxWidth: '269px' }}
											alt={title}
										/>
									) : (
										<CircularProgress color='inherit' size={20} />
									)}
								</Grid>
							</Grid>
						</div>
					</Grid>
				)}

				{(((deletePicture || !(isAdditional ? statsImageId : imageId) || (isAdditional ? statsImageId : imageId)?.length === 0) &&
					!File) ||
					isCropping) && (
					<Grid item style={{ width: '296px', height: '214px' }}>
						<DropzoneArea
							dropzoneProps={{ disabled: isReadOnly }}
							dropzoneClass={isReadOnly ? classes.noDrop : classes.root}
							dropzoneText={t('common.form.uploadImage')}
							onChange={(files) => {
								if (!isReadOnly) {
									setPicture(files[0] ?? null);
									setFile(files[0] ?? null);
									setDeletePicture(files[0] ? false : true);
									if (files[0]) {
										setValue(`delete${isAdditional && 'Stats'}File`, false);
										setIsCropping(true);
									}
								}
							}}
							filesLimit={1}
							acceptedFiles={['image/gif', 'image/jpeg', 'image/png', 'image/jfif']}
							maxFileSize={1024 * 1024 * 5}
							showPreviewsInDropzone={true}
							useChipsForPreview={false}
							previewGridProps={{
								item: { style: { minWidth: '269px' } },
								container: { style: { maxWidth: '269px' } },
							}}
							alertSnackbarProps={{ anchorOrigin: { vertical: 'top', horizontal: 'center' } }}
							getFileLimitExceedMessage={(filesLimit) => t('common.form.dropzone.fileLimitMessage') + filesLimit}
							getFileAddedMessage={(fileName) => t('common.form.dropzone.fileAddedMessage') + fileName}
							getFileRemovedMessage={(fileName) => t('common.form.dropzone.fileRemovedMessage') + fileName}
							getDropRejectMessage={(rejectedFile, acceptedFiles, maxFileSize) => {
								let message = t('common.form.dropzone.fileRejectedMessage') + rejectedFile.name + ' ';
								if (!acceptedFiles.includes(rejectedFile.type)) {
									message += t('common.form.dropzone.fileTypeNotSupported');
								}
								if (rejectedFile.size > maxFileSize) {
									message += t('common.form.dropzone.fileExceededMaxSize') + convertBytesToMbsOrKbs(maxFileSize);
								}
								return message;
							}}
						/>
					</Grid>
				)}
			</Grid>

			{isCropping && (
				<CropImageDialog
					isOpen={isCropping}
					setOpen={(e) => setIsCropping(e)}
					pictureUrl={File!}
					setCroppedImage={(url, path) => {
						setFile(blobToFile(path!, url!));
						setPicture(blobToFile(path!, url!));
						setImageStr(url);
						!isAdditional && setValue('image', 'true');
						triggerValidation('image');
					}}
					imageDimensions={imageDimensions}
				/>
			)}
		</>
	);
}
